import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { push } from 'redux-first-history';
import { compose } from 'react-recompose';
import styled, { keyframes } from 'styled-components';

import { Box, Link } from '@material-ui/core';
import { Typography, Panel, ContentLoader, CampaignIcon, ElasticTheme as Theme, palette, formatNumber } from '@dev/ee-components';

import dayjs from '@api/services/tools/dayjs';

import { LoadTodaySummaryStart, LoadSummaryStartParams } from '@api/store/statistics/actions';
import { LoadInprogressStatsStartDelayed } from '@api/store/logs/actions';
import { ApplicationState as State } from '@api/store';
import StatisticsSummaryInterface from '../StatisticsSummaryInterface';
import { IntervalType } from '@dev/ee-api/ee-api-models';
import OpenedIcon from '../../icons/opened.svg';
import ClickedIcon from '../../icons/clicked.svg';
import BouncedIcon from '../../icons/bounced.svg';
import { isEmpty } from 'lodash-es';
import { StatisticsPaths } from '@api/services/dictionaries/paths';
const mapState = (state: State, ownProps: StatisticsSummaryInterface): StatisticsSummaryInterface => {
    return {
        ...ownProps,
        browser: state.browser,
        statistics: state.statistics,
        logs: state.logs,
        account: state.account,
    };
};

const dispatchProps = {
    LoadInprogressStatsStartDelayed,
    LoadTodaySummaryStart,
    Push: push,
};

type DispatchProps = {
    LoadInprogressStatsStartDelayed(inProgressActivePage?: number);
    LoadTodaySummaryStart(params: LoadSummaryStartParams);
    Push(url: string): void;
};

type Props = StatisticsSummaryInterface & DispatchProps;

type TopContent = {
    isMobile: boolean;
};
const TopContent = styled.div<TopContent>`
    width: 100%;
    display: flex;
    justify-content: ${(props): string => (props.isMobile ? 'flex-start' : 'space-between')};
    flex-direction: ${(props): string => (props.isMobile ? 'column' : 'row')};
    margin-top: 32px;
`;

type InProgressPanelType = {
    isMobile: boolean;
};
const InProgressPanel = styled.div<InProgressPanelType>`
    width: ${(props): string => (props.isMobile ? '100%' : '35%')};
    margin: ${(props): string => (props.isMobile ? '0 0 3rem 0' : ' 0 3rem 0 0')};
`;

const InProgressCountContainer = styled.div`
    max-width: 45rem;
`;

const waves = keyframes`
    40% {
        transform: translateY(0px);
    }

    50% {
        transform: translateY(-5px);
    }
`;

const DotsContainer = styled.div<{ visible: boolean }>`
    visibility: ${(props): string => (props.visible ? 'visible' : 'hidden')};
    display: inline-block;
    margin-left: 5px;
`;

const Dot = styled.span`
    display: inline-block;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    margin-right: 3px;
    background-color: ${palette.jacarta[5]};
    animation: ${waves} 1.2s linear infinite;

    &:nth-child(2) {
        animation-delay: 0.4s;
    }

    &:nth-child(3) {
        animation-delay: 0.8s;
    }
`;

const TodayPanel = styled.div<InProgressPanelType>`
    width: ${(props): string => (props.isMobile ? '100%' : ' 65%')};
`;

const TodayLogsCountContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
`;

const TodayLogCount = styled.div<{ isMobile: boolean }>`
    width: ${(props): string => (props.isMobile ? '6rem' : 'auto')};
    padding: ${(props): string => (props.isMobile ? '8px' : '0')};
`;

const ContentLoaderWrapper = styled.div`
    margin-right: 16px;
    min-width: 45px;
    flex: 1;
`;

const IconContainer = styled.div`
    margin-left: 8px;
    display: inline-block;
`;

const SkeletonWrapper = styled.div`
    display: block;
`;

class SummaryTopContent extends Component<Props> {
    public state = {
        inprogressStats: 9,
        from: null,
        to: null,
    };

    public componentDidMount(): void {
        const { LoadInprogressStatsStartDelayed, LoadTodaySummaryStart } = this.props;
        const from = this.state.from || dayjs().subtract(30, 'day').startOf('day').utc().format();
        const to = this.state.to || dayjs().endOf('day').utc().format();
        const todayFrom = dayjs().startOf('day').utc().format();

        LoadTodaySummaryStart({ from: todayFrom, to, interval: IntervalType.Hourly });
        LoadInprogressStatsStartDelayed(1);

        this.setState({ from, to });
    }

    public shouldComponentUpdate(nextProps): boolean {
        const currCount = this.props.logs.inprogressStatsDelayed?.Count;
        const nextCount = nextProps.logs.inprogressStatsDelayed?.Count;
        const currLoader = this.props.logs.setLoader;
        const nextLoader = nextProps.logs.setLoader;
        const currDelayedLoading = this.props.logs.inProgressDelayedLoading;
        const nextDelayedLoading = nextProps.logs.inProgressDelayedLoading;

        let refresh = true;

        if (
            currDelayedLoading === nextDelayedLoading &&
            currCount === nextCount &&
            currLoader === nextLoader &&
            !this.props.statistics.todaySummaryLoading
        ) {
            refresh = false;
        }
        return refresh;
    }

    private inProgressCountPanel = (): JSX.Element => {
        const {
            t,
            logs: { inProgressDelayedLoading, inprogressStatsDelayed, setLoader },
        } = this.props;

        const { Overview, accountLoading } = this.props.account;

        if (accountLoading) {
            return (
                <InProgressCountContainer>
                    <SkeletonWrapper>
                        <ContentLoader mode="circle" height={30} width={30} />
                    </SkeletonWrapper>
                    <SkeletonWrapper>
                        <ContentLoader mode="row" height={18} width={180} />
                    </SkeletonWrapper>
                </InProgressCountContainer>
            );
        }

        const count = inProgressDelayedLoading && !inprogressStatsDelayed?.Count ? Overview.InProgressCount : inprogressStatsDelayed?.Count;

        return (
            <InProgressCountContainer>
                <Typography variant={'h1'}>{formatNumber(count) ?? 0}</Typography>
                <Typography variant={'subtitle1'}>
                    <Box color={Theme.typography.h1.color} fontWeight={Theme.typography.h1.fontWeight}>
                        {t('allinprogress')}
                        {this.showDots(setLoader)}
                    </Box>
                </Typography>
            </InProgressCountContainer>
        );
    };

    private showDots = (setLoader): JSX.Element => {
        return (
            <DotsContainer visible={setLoader}>
                <Dot />
                <Dot />
                <Dot />
            </DotsContainer>
        );
    };

    private inProgressLinkPanel = (): JSX.Element => {
        const { t, Push } = this.props;

        return (
            <div>
                <Typography variant="body1">
                    <Box>
                        <Link variant="body1" onClick={(): void => Push(StatisticsPaths.StatisticsInprogress)}>
                            {t('viewinprogress')}
                        </Link>
                    </Box>
                </Typography>
            </div>
        );
    };

    private todayLogsCountPanel = (): JSX.Element => {
        const {
            t,
            statistics: { todaySummary },
            browser,
        } = this.props;

        const isMobile = browser.lessThan.sm;

        return (
            <TodayLogsCountContainer>
                <TodayLogCount isMobile={isMobile}>
                    <Typography variant={'h1'}>
                        {formatNumber(todaySummary?.LogStatusSummary?.Recipients || 0)}
                        <IconContainer>
                            <CampaignIcon />
                        </IconContainer>
                    </Typography>
                    <Typography variant={'subtitle1'} display="inline">
                        <Box color={Theme.typography.h1.color} fontWeight={Theme.typography.h1.fontWeight}>
                            {t('submitted')}
                        </Box>
                    </Typography>
                </TodayLogCount>
                <TodayLogCount isMobile={isMobile}>
                    <Typography variant={'h1'}>
                        {formatNumber(todaySummary?.LogStatusSummary?.Opened || 0)}
                        <IconContainer>
                            <OpenedIcon />
                        </IconContainer>
                    </Typography>
                    <Typography variant={'subtitle1'} display="inline">
                        <Box color={Theme.typography.h1.color} fontWeight={Theme.typography.h1.fontWeight}>
                            {t('opened')}
                        </Box>
                    </Typography>
                </TodayLogCount>
                <TodayLogCount isMobile={isMobile}>
                    <Typography variant={'h1'}>
                        {formatNumber(todaySummary?.LogStatusSummary?.Clicked || 0)}
                        <IconContainer>
                            <ClickedIcon />
                        </IconContainer>
                    </Typography>
                    <Typography variant={'subtitle1'} display="inline">
                        <Box color={Theme.typography.h1.color} fontWeight={Theme.typography.h1.fontWeight}>
                            {t('clicked')}
                        </Box>
                    </Typography>
                </TodayLogCount>
                <TodayLogCount isMobile={isMobile}>
                    <Typography variant={'h1'}>
                        {formatNumber(todaySummary?.LogStatusSummary?.Bounced || 0)}
                        <IconContainer>
                            <BouncedIcon />
                        </IconContainer>
                    </Typography>
                    <Typography variant={'subtitle1'} display="inline">
                        <Box color={Theme.typography.h1.color} fontWeight={Theme.typography.h1.fontWeight}>
                            {t('bounced')}
                        </Box>
                    </Typography>
                </TodayLogCount>
            </TodayLogsCountContainer>
        );
    };

    private todayDataLoader = (): JSX.Element => {
        return (
            <TodayLogsCountContainer>
                <ContentLoaderWrapper>
                    <ContentLoader mode={'row'} height={20} count={2} />
                </ContentLoaderWrapper>
                <ContentLoaderWrapper>
                    <ContentLoader mode={'row'} height={20} count={2} />
                </ContentLoaderWrapper>
                <ContentLoaderWrapper>
                    <ContentLoader mode={'row'} height={20} count={2} />
                </ContentLoaderWrapper>
                <ContentLoaderWrapper>
                    <ContentLoader mode={'row'} height={20} count={2} />
                </ContentLoaderWrapper>
            </TodayLogsCountContainer>
        );
    };

    private todayLinkPanel = (): JSX.Element => {
        const { t, Push } = this.props;

        return (
            <div>
                <Typography variant="body1">
                    <Box>
                        <Link variant="body1" onClick={(): void => Push('/logs/email')}>
                            {t('viewemailslog')}
                        </Link>
                    </Box>
                </Typography>
            </div>
        );
    };

    public render(): JSX.Element {
        const {
            browser,
            statistics: { todaySummary, todaySummaryLoading },
            t,
        } = this.props;
        const isMobile = browser.lessThan.sm;

        return (
            <TopContent isMobile={isMobile}>
                <InProgressPanel isMobile={isMobile}>
                    <Panel header={t('inprogress')} elementPadding={'24px 32px'} headerPadding={'24px 32px 16px 32px'}>
                        {this.inProgressCountPanel()}
                        {this.inProgressLinkPanel()}
                    </Panel>
                </InProgressPanel>
                <TodayPanel isMobile={isMobile}>
                    <Panel header={t('today')} elementPadding={'24px 32px'} headerPadding={'24px 32px 16px 32px'}>
                        {todaySummaryLoading || isEmpty(todaySummary) ? this.todayDataLoader() : this.todayLogsCountPanel()}
                        {this.todayLinkPanel()}
                    </Panel>
                </TodayPanel>
            </TopContent>
        );
    }
}

export default compose(withTranslation(), connect<StatisticsSummaryInterface, DispatchProps>(mapState, dispatchProps))(SummaryTopContent);
